import React, { useState, useEffect } from 'react';
import { Song } from '../types';
import { searchSongs, sanitizeSongTitle, checkVideoAgeRestriction, resolveSongFromLink } from '../services/youtube';
import { motion, AnimatePresence } from 'framer-motion';

interface SongSearchProps {
  onSongSelect: (song: Song) => void;
  /** Error from the server about the last confirmed pick (e.g. video too long), shown inline. */
  externalError?: string;
  onClearExternalError?: () => void;
}

// Quick client-side check so a YouTube URL typed into the search box is treated as a link.
const looksLikeYouTubeLink = (value: string): boolean =>
  /(?:youtube\.com|youtu\.be|youtube-nocookie\.com)\//i.test(value.trim());

// YouTube's search API returns titles with HTML entities (e.g. "&#39;", "&amp;").
const decodeHtmlEntities = (value: string): string => {
  if (!/&[#\w]+;/.test(value)) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const SongSearch: React.FC<SongSearchProps> = ({ onSongSelect, externalError = '', onClearExternalError }) => {
  const [query, setQuery] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(false);
  const [titleLoading, setTitleLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const [startTimeSeconds, setStartTimeSeconds] = useState(0);
  const [isAgeRestricted, setIsAgeRestricted] = useState(false);
  const MAX_TITLE_LENGTH = 50;
  const MAX_START_TIME = 120; // 2 minutes in seconds
  const MAX_VIDEO_DURATION_SECONDS = 10 * 60;
  const MAX_VIDEO_DURATION_LABEL = '10 minutes';

  // Surface a server-side rejection of the previous pick in the search view.
  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  const clearError = () => {
    setError('');
    if (externalError) {
      onClearExternalError?.();
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    // If someone pastes a YouTube URL into the search box, treat it as a link instead of a search.
    if (looksLikeYouTubeLink(query)) {
      await resolveLink(query);
      return;
    }
    
    setLoading(true);
    clearError();
    
    try {
      const results = (await searchSongs(query)).map((song) => ({
        ...song,
        title: decodeHtmlEntities(song.title),
        channelTitle: decodeHtmlEntities(song.channelTitle)
      }));
      setSongs(results);
      
      if (results.length === 0) {
        setError('No songs found');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error searching for songs';
      const isQuotaError = /quota/i.test(message);
      setError(
        isQuotaError
          ? `${message} You can still pick a song by pasting a YouTube link below.`
          : message
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Resolve a pasted YouTube link into a song and jump straight to the preview step.
  const resolveLink = async (rawLink: string) => {
    const link = rawLink.trim();
    if (!link) {
      setError('Please paste a YouTube link');
      return;
    }

    setLinkLoading(true);
    clearError();

    try {
      const resolved = await resolveSongFromLink(link);
      const song: Song = {
        videoId: resolved.videoId,
        title: resolved.title,
        thumbnail: resolved.thumbnail,
        channelTitle: resolved.channelTitle,
        durationSeconds: resolved.durationSeconds
      };

      // If the backend couldn't determine the age rating (e.g. quota exhausted, oEmbed fallback),
      // don't block the user: the download step will fail loudly if the video is unusable.
      await handleSongClick(song, {
        knownAgeRestriction: resolved.isAgeRestricted ?? false,
        initialStartSeconds: resolved.startSeconds
      });
      setLinkInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that YouTube link');
      console.error(err);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await resolveLink(linkInput);
  };

  interface SongClickOptions {
    // When defined, skip the extra age-restriction API call (already known from the link resolver).
    knownAgeRestriction?: boolean;
    // Start time parsed from a "t=" param in a pasted link.
    initialStartSeconds?: number;
  }

  const handleSongClick = async (song: Song, options: SongClickOptions = {}) => {
    if (song.durationSeconds && song.durationSeconds > MAX_VIDEO_DURATION_SECONDS) {
      setError(`Videos longer than ${MAX_VIDEO_DURATION_LABEL} cannot be selected.`);
      return;
    }

    const initialStart = Math.min(
      MAX_START_TIME,
      Math.max(0, Math.floor(options.initialStartSeconds ?? 0))
    );

    setSelectedSong(song);
    setTitleLoading(true);
    setIsAgeRestricted(false); // Reset age restriction flag
    clearError(); // Clear any previous errors
    // Don't initialize with original title
    setCustomTitle('');
    setStartTimeSeconds(initialStart);
    
    try {
      // Check if the video is age-restricted (skip the API call if the link resolver already told us).
      const isRestricted =
        options.knownAgeRestriction !== undefined
          ? options.knownAgeRestriction
          : await checkVideoAgeRestriction(song.videoId);
      
      if (isRestricted) {
        setIsAgeRestricted(true);
        setError('This video is age-restricted and cannot be selected.');
        setTitleLoading(false);
        return;
      }
      
      // Get AI-sanitized title from backend
      const sanitizedTitle = await sanitizeSongTitle(song.title);
      setCustomTitle(sanitizedTitle);
    } catch (error) {
      console.error('Failed to process song:', error);
      setCustomTitle(song.title); // Fallback to original title
    } finally {
      setTitleLoading(false);
    }
  };

  const handleCustomTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_TITLE_LENGTH) {
      setCustomTitle(value);
    }
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seconds = parseInt(e.target.value, 10);
    setStartTimeSeconds(seconds);
  };

  // Format seconds to MM:SS format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleConfirm = () => {
    if (selectedSong && !isAgeRestricted) {
      if (selectedSong.durationSeconds && selectedSong.durationSeconds > MAX_VIDEO_DURATION_SECONDS) {
        setError(`Videos longer than ${MAX_VIDEO_DURATION_LABEL} cannot be selected.`);
        return;
      }

      // Create a copy of the selected song with the custom title and start time
      const songWithCustomTitle = {
        ...selectedSong,
        title: customTitle.trim() || selectedSong.title,
        startTime: formatTime(startTimeSeconds)
      };
      
      onSongSelect(songWithCustomTitle);
    }
  };

  const titleTooLong = customTitle.length > MAX_TITLE_LENGTH * 0.8;
  const startPercent = (startTimeSeconds / MAX_START_TIME) * 100;

  const handleBackToSearch = () => {
    setSelectedSong(null);
    clearError(); // Clear the error when going back to search
  };

  return (
    <div className="song-search">
      <AnimatePresence mode="wait">
      {!selectedSong ? (
        <motion.div
          key="search-view"
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 }}
          transition={{ duration: 0.25 }}
        >
          <form onSubmit={handleSearch} className="search-bar">
            <span className="search-bar-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <input
              className="input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for a song or artist"
              aria-label="Search for a song"
              disabled={linkLoading}
              autoFocus
            />
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || linkLoading}
            >
              {loading ? (
                <>
                  <span className="spinner spinner--sm" /> Searching…
                </>
              ) : (
                'Search'
              )}
            </button>
          </form>

          <div className="divider">
            <span>or paste a YouTube link</span>
          </div>

          <form onSubmit={handleLinkSubmit} className="search-bar search-bar--link">
            <span className="search-bar-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10 14a4 4 0 0 0 5.66 0l2.83-2.83a4 4 0 1 0-5.66-5.66l-1.06 1.06M14 10a4 4 0 0 0-5.66 0l-2.83 2.83a4 4 0 1 0 5.66 5.66l1.06-1.06"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <input
              className="input"
              type="text"
              inputMode="url"
              autoComplete="off"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              aria-label="YouTube link"
              disabled={loading}
            />
            <button 
              type="submit" 
              className="btn btn-secondary"
              disabled={loading || linkLoading}
            >
              {linkLoading ? (
                <>
                  <span className="spinner spinner--sm" /> Loading…
                </>
              ) : (
                'Use link'
              )}
            </button>
          </form>
          
          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="alert alert--error" role="alert">
              {error}
            </motion.div>
          )}
          
          {songs.length > 0 && (
            <div className="results-heading">
              <span>{songs.length} results</span>
              <span className="muted">Click a song to preview it</span>
            </div>
          )}

          <div className="songs-grid">
            <AnimatePresence>
              {songs.map((song, index) => (
                <motion.button
                  type="button"
                  key={song.videoId}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.04, 0.4) }}
                  className="song-card"
                  onClick={() => handleSongClick(song)}
                >
                  <div className="song-thumb">
                    <img src={song.thumbnail} alt="" loading="lazy" />
                    {song.durationSeconds && (
                      <span className="duration-badge">{formatTime(song.durationSeconds)}</span>
                    )}
                    <span className="song-thumb-play" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                  <div className="song-card-body">
                    <h3>{song.title}</h3>
                    <p>{song.channelTitle}</p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      ) : (
        <motion.div 
          key="preview-view"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="song-preview"
        >
          <div className="song-preview-head">
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleBackToSearch}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to results
            </button>
            <h3>Preview &amp; confirm</h3>
          </div>
          
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="alert alert--error"
              role="alert"
            >
              {error}
            </motion.div>
          )}
          
          <div className={`song-preview-grid${isAgeRestricted ? ' song-preview-grid--single' : ''}`}>
            <div className="video-frame">
              <iframe
                src={`https://www.youtube.com/embed/${selectedSong.videoId}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          
            {!isAgeRestricted && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="song-preview-controls"
              >
                <div className="field">
                  <label className="field-label" htmlFor="customTitle">
                    Title players must guess
                  </label>
                  <p className="field-hint">
                    Trim it down to just the song name. Drop the artist, “official video”, and similar extras.
                  </p>
                  
                  {titleLoading ? (
                    <div className="input input--static" aria-live="polite">
                      <span className="spinner spinner--sm" />
                      <span>Extracting song name…</span>
                    </div>
                  ) : (
                    <input
                      className={`input${titleTooLong ? ' input--warn' : ''}`}
                      type="text"
                      id="customTitle"
                      value={customTitle}
                      onChange={handleCustomTitleChange}
                      placeholder="Song title"
                      maxLength={MAX_TITLE_LENGTH}
                    />
                  )}
                  
                  {!titleLoading && (
                    <div className="field-footer">
                      <span className={titleTooLong ? 'warn-text' : ''}>
                        {titleTooLong && 'Getting long — shorter titles are easier to guess'}
                      </span>
                      <span className={`char-count${titleTooLong ? ' warn-text' : ''}`}>
                        {customTitle.length}/{MAX_TITLE_LENGTH}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="field">
                  <div className="field-label-row">
                    <label className="field-label" htmlFor="startTime">
                      Start playback at
                    </label>
                    <span className="mono-pill">{formatTime(startTimeSeconds)}</span>
                  </div>
                  <input
                    type="range"
                    id="startTime"
                    className="range"
                    min="0"
                    max={MAX_START_TIME}
                    value={startTimeSeconds}
                    onChange={handleStartTimeChange}
                    style={{ ['--pct' as string]: `${startPercent}%` }}
                  />
                  <div className="range-scale">
                    <span>0:00</span>
                    <span>1:00</span>
                    <span>2:00</span>
                  </div>
                  <p className="field-hint">Players hear 60 seconds starting from this point.</p>
                </div>
              </motion.div>
            )}
          </div>
          
          <div className="cta-row cta-row--end">
            <button type="button" className="btn btn-secondary" onClick={handleBackToSearch}>
              Choose another
            </button>
            <button 
              type="button"
              className="btn btn-primary" 
              onClick={handleConfirm}
              disabled={titleLoading || isAgeRestricted}
            >
              Confirm selection
            </button>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default SongSearch;
